import * as React from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  useAchievements,
  useUserAchievements,
  useAchievementProgress,
  useAchievementStats,
  useCheckAchievements,
} from '@/hooks/useAchievements';
import { useXpBreakdown, getActivityLabel } from '@/hooks/useXpBreakdown';
import { AchievementCard } from '@/components/AchievementCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Trophy, Lock, CheckCircle2, Star, TrendingUp, Award } from 'lucide-react';
import { AchievementCategory, categoryLabels } from '@/types/achievements';
import { Skeleton } from '@/components/ui/skeleton';
import { StreakDisplay } from '@/components/StreakDisplay';
import { Progress } from '@/components/ui/progress';
import { calculateXpForLevel, calculateXpForNextLevel } from '@/lib/xpUtils';

export default function Achievements() {
  const { user } = useAuth();
  const { data: allAchievements, isLoading: achievementsLoading } = useAchievements();
  const { data: userAchievements, isLoading: userAchievementsLoading } = useUserAchievements(user?.id);
  const { data: progressData } = useAchievementProgress(user?.id);
  const { data: stats } = useAchievementStats(user?.id);
  const { data: xpBreakdown } = useXpBreakdown(user?.id);
  const checkAchievements = useCheckAchievements();

  const [selectedCategory, setSelectedCategory] = React.useState<AchievementCategory | 'all'>('all');

  // Check for new achievements when component mounts
  React.useEffect(() => {
    if (user?.id) {
      checkAchievements.mutate(user.id);
    }
  }, [user?.id]);

  const isLoading = achievementsLoading || userAchievementsLoading;

  const unlockedIds = new Set(userAchievements?.map((ua) => ua.achievement_id) || []);
  const progressMap = new Map(
    progressData?.map((p) => [
      p.achievement_id,
      { current: p.current_value, target: p.target_value },
    ]) || []
  );

  const filteredAchievements = React.useMemo(() => {
    if (!allAchievements) return [];
    
    let filtered = allAchievements;
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((a) => a.category === selectedCategory);
    }

    return filtered.map((achievement) => ({
      achievement,
      unlocked: unlockedIds.has(achievement.id),
      progress: progressMap.get(achievement.id),
      unlockedAt: userAchievements?.find((ua) => ua.achievement_id === achievement.id)?.unlocked_at,
    }));
  }, [allAchievements, selectedCategory, unlockedIds, progressMap, userAchievements]);

  const unlocked = filteredAchievements.filter((a) => a.unlocked);
  const locked = filteredAchievements.filter((a) => !a.unlocked);

  if (isLoading) {
    return (
      <div className="w-full max-w-full overflow-x-hidden">
        <div className="container mx-auto px-4 py-8 max-w-6xl w-full">
          <Skeleton className="h-12 w-64 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full overflow-x-hidden">
      <div className="container mx-auto px-4 py-8 max-w-6xl w-full">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-2 sm:gap-3">
          <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
          Achievements
        </h1>

        {user && (
          <div className="mb-4">
            <StreakDisplay />
          </div>
        )}

        {stats && (
          <>
            {/* Level Display Card */}
            <Card className="mb-4 bg-gradient-to-br from-primary/10 via-primary/5 to-background border-primary/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-primary/20 p-4 rounded-full">
                      <Star className="w-8 h-8 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Current Level</p>
                      <h2 className="text-2xl sm:text-3xl font-bold">Level {stats.level || 1}</h2>
                      <p className="text-lg text-primary font-medium">{stats.level_title || 'Bookworm Beginner'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground mb-1">XP Progress</p>
                    <p className="text-2xl font-bold text-primary">{stats.total_xp || 0}</p>
                    <p className="text-xs text-muted-foreground">
                      {calculateXpForNextLevel(stats.level || 1)} XP to next level
                    </p>
                  </div>
                </div>
                <div className="mt-4">
                  <Progress 
                    value={
                      stats.level 
                        ? ((stats.total_xp || 0) - calculateXpForLevel(stats.level)) / 
                          calculateXpForNextLevel(stats.level) * 100
                        : 0
                    }
                    className="h-3"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Achievement Stats Card */}
            <Card className="mb-4">
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary">{stats.unlocked_count}</p>
                    <p className="text-sm text-muted-foreground">Unlocked</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">{stats.total_achievements}</p>
                    <p className="text-sm text-muted-foreground">Total</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary">
                      {stats.completion_percent}%
                    </p>
                    <p className="text-sm text-muted-foreground">Complete</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary">
                      {stats.total_xp_from_achievements}
                    </p>
                    <p className="text-sm text-muted-foreground">Achievement XP</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* XP Breakdown Card */}
            {xpBreakdown && xpBreakdown.items.length > 0 && (
              <Card className="mb-4">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    How You Earned XP
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {xpBreakdown.items.slice(0, 7).map((item) => {
                      const activityInfo = getActivityLabel(item.activity_type);
                      return (
                        <div key={item.activity_type} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-2">
                              <span className="text-base">{activityInfo.icon}</span>
                              <span className="font-medium">{activityInfo.label}</span>
                            </span>
                            <span className="text-muted-foreground">
                              {item.total_xp} XP ({item.percentage}%)
                            </span>
                          </div>
                          <Progress value={item.percentage} className="h-2" />
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      <Tabs defaultValue="all" className="mb-6">
        <TabsList>
          <TabsTrigger value="all" onClick={() => setSelectedCategory('all')}>
            All
          </TabsTrigger>
          <TabsTrigger value="unlocked" onClick={() => setSelectedCategory('all')}>
            <CheckCircle2 className="w-4 h-4 mr-1" />
            Unlocked
          </TabsTrigger>
          <TabsTrigger value="locked" onClick={() => setSelectedCategory('all')}>
            <Lock className="w-4 h-4 mr-1" />
            Locked
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6">
          <div className="flex gap-2 mb-4 flex-wrap">
            {['all', ...(Object.keys(categoryLabels) as AchievementCategory[])].map((cat) => {
              const getCategoryIcon = (category: string) => {
                switch(category) {
                  case 'awards': return <Award className="w-3.5 h-3.5 mr-1" />;
                  case 'reading': return <span className="mr-1">📚</span>;
                  case 'social': return <span className="mr-1">👥</span>;
                  case 'streak': return <span className="mr-1">🔥</span>;
                  case 'collection': return <span className="mr-1">📖</span>;
                  case 'milestone': return <span className="mr-1">🎯</span>;
                  default: return null;
                }
              };

              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat as AchievementCategory | 'all')}
                  className={`px-3 py-1 rounded-full text-sm transition-colors flex items-center ${
                    selectedCategory === cat
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted hover:bg-muted/80'
                  }`}
                >
                  {cat !== 'all' && getCategoryIcon(cat)}
                  {cat === 'all' ? 'All' : categoryLabels[cat as AchievementCategory]}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredAchievements.map((item) => (
              <AchievementCard
                key={item.achievement.id}
                achievement={item.achievement}
                unlocked={item.unlocked}
                unlockedAt={item.unlockedAt}
                progress={item.progress}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="unlocked" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {unlocked.map((item) => (
              <AchievementCard
                key={item.achievement.id}
                achievement={item.achievement}
                unlocked={item.unlocked}
                unlockedAt={item.unlockedAt}
              />
            ))}
          </div>
          {unlocked.length === 0 && (
            <p className="text-center text-muted-foreground py-12">
              No achievements unlocked yet. Start reading to unlock your first achievement!
            </p>
          )}
        </TabsContent>

        <TabsContent value="locked" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {locked.map((item) => (
              <AchievementCard
                key={item.achievement.id}
                achievement={item.achievement}
                unlocked={false}
                progress={item.progress}
              />
            ))}
          </div>
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
}
